import { useCallback, useState } from 'react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, ButtonBase, Collapse, Paper, Stack, Typography } from '@mui/material';
import { readSectionOpen, writeSectionOpen } from '../../lib/sectionState';

/**
 * A panel the host can fold away.
 *
 * The guest table is the reason this exists: with 137 rows it wants the whole
 * screen, and the tools above it — import, export, invites — are things a host
 * touches once and then scrolls past for the rest of the trip. Folding them
 * away is not decoration, it is the difference between reading the list and
 * hunting for it.
 *
 * Children stay mounted while collapsed. These panels hold half-finished work
 * (a parsed import awaiting column mapping, a composed message), and folding a
 * section must never be a way to lose it.
 */
export default function CollapsibleSection({
  id,
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  /** Stable key for the remembered open/closed state. Shared across trips. */
  id: string;
  title: string;
  /** What is inside, shown while collapsed so the panel stays discoverable. */
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  // Read once, on mount. defaultOpen is deliberately NOT tracked afterwards:
  // it depends on the data (an empty guest list opens the import tools), and
  // re-applying it would fold the panel the moment the data changed — which is
  // exactly when a host is reading the import result inside it. First render
  // decides; after that only the host does.
  const [open, setOpen] = useState(() => readSectionOpen(id) ?? defaultOpen);

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      writeSectionOpen(id, !wasOpen);
      return !wasOpen;
    });
  }, [id]);

  const regionId = `section-${id}`;

  // The header carries the surface, not a wrapper around the children: these
  // panels already render their own cards, and nesting one inside another
  // produces a border inside a border.
  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <ButtonBase
          onClick={toggle}
          aria-expanded={open}
          aria-controls={regionId}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            textAlign: 'left',
            px: 2.5,
            py: 1.5,
            borderRadius: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
            <ExpandMoreRoundedIcon
              fontSize="small"
              sx={{
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 150ms',
                color: 'text.secondary',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} variant="body2">
                {title}
              </Typography>
              {!open && summary ? (
                <Typography variant="caption" color="text.secondary">
                  {summary}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </ButtonBase>
      </Paper>
      <Collapse in={open}>
        <Box id={regionId} sx={{ mt: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
